import { AlertTriangle, Calendar, CarFront, ChevronDown, PauseCircle, Plus, ThumbsDown, Wrench } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { updateQuoteSubStatus } from '../../api/api'
import { SubStatusEnum } from '../../enums/enums'
import { Logger } from '../../logger'

const ICON_MAP = {
  Wrench,
  Calendar,
  PauseCircle,
  CarFront,
  ThumbsDown,
  AlertTriangle
}

const ORDERED_VALUES = [
  SubStatusEnum.WIP.value,
  SubStatusEnum.SCHEDULED.value,
  SubStatusEnum.ON_HOLD.value,
  SubStatusEnum.TOTAL_LOSS.value,
  SubStatusEnum.REJECTED.value,
  SubStatusEnum.MISSED.value
]

const subStatusByValue = (value) => {
  if (!value) return null
  return Object.values(SubStatusEnum).find((s) => s.value === value) || null
}

/** Renders the inline "icon-tile + label" pill used both for the trigger and
 *  each dropdown option. Pass `showChevron` for the trigger variant. */
const SubStatusChip = ({ subStatus, showChevron = false }) => {
  if (!subStatus) {
    return (
      <span className='inline-flex items-center justify-center rounded-full bg-[#F5F7FA] px-2 py-1 text-[#6B7280]'>
        <Plus className='h-3.5 w-3.5' />
      </span>
    )
  }
  const Icon = ICON_MAP[subStatus.iconName]
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-[#F5F7FA] py-1 pl-1 pr-2.5 text-[12px] font-semibold text-[#2C3D4D]'>
      <span className='inline-flex h-5 w-5 flex-none items-center justify-center rounded-full' style={{ backgroundColor: subStatus.tile }}>
        {Icon && <Icon className='h-3 w-3' style={{ color: subStatus.icon }} />}
      </span>
      {subStatus.label}
      {showChevron && <ChevronDown className='h-3 w-3 text-[#6B7280]' />}
    </span>
  )
}

SubStatusChip.propTypes = {
  subStatus: PropTypes.object,
  showChevron: PropTypes.bool
}

/** Cal-center sub-status picker (CAR-3491).
 *  Sits under the OPEN main-status pill on the Quotes queue card.
 *
 *  - When `closedStatus` is true → renders a static read-only chip; no Popover.
 *  - Otherwise → click opens a Popover with Live (WIP / Scheduled / On Hold)
 *    above a "— LOST OUTCOMES —" divider and Lost (Total Loss / Rejected /
 *    Missed) below. One click commits via PUT /v1/quotes/{quoteId}/substatus.
 *
 *  Tooltip on hover surfaces the audit metadata from `setBy` / `setAt` when
 *  the BE provides it (AC7).
 */
// Approximate rendered menu height (header + 6 options + lost-outcomes divider).
// Only used to decide whether to flip the menu above the trigger.
const MENU_MAX_HEIGHT = 280
const MENU_WIDTH = 224 // w-56

export const SubStatusPicker = ({ quoteId, currentSubStatus, closedStatus: closedStatusProp = false, onChange, setBy, setAt }) => {
  // Picking a sub-status persists immediately; a client user sees the current value only.
  const isReadOnlyUser = useIsReadOnly()
  const closedStatus = closedStatusProp || isReadOnlyUser
  const [value, setValue] = useState(currentSubStatus || '')
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Fixed-position coordinates for the portal-rendered menu. `openUp` anchors the
  // menu's bottom to the trigger top so it isn't clipped by the page footer / table
  // overflow on the last rows of the list.
  const [menuPos, setMenuPos] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    setValue(currentSubStatus || '')
  }, [currentSubStatus])

  useEffect(() => {
    if (!open) return undefined

    const reposition = () => {
      const el = triggerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openUp = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8))
      setMenuPos(openUp ? { left, bottom: window.innerHeight - rect.top + 6, openUp } : { left, top: rect.bottom + 6, openUp })
    }
    reposition()

    const onMouseDown = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target)
      const inMenu = menuRef.current?.contains(e.target)
      if (!inTrigger && !inMenu) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    // Capture-phase so we catch scrolls on any ancestor scroll container, not just window.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  const selected = subStatusByValue(value)
  const tooltip = setBy && setAt ? `Set by ${setBy}, ${setAt}` : undefined

  if (closedStatus) {
    return (
      <span title={tooltip}>
        <SubStatusChip subStatus={selected} />
      </span>
    )
  }

  const onPick = async (next) => {
    if (isSaving) return
    setIsSaving(true)
    try {
      const res = await updateQuoteSubStatus({ quoteId, subStatus: next })
      if (res?.status === 200 || res?.status === 204) {
        setValue(next)
        setOpen(false)
        onChange?.(next, res?.data)
      }
    } catch (err) {
      Logger.error({ message: err, payload: { file: 'SubStatusPicker', quoteId, next } })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='relative inline-block'>
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen((o) => !o)}
        disabled={isSaving}
        title={tooltip}
        className='inline-flex items-center transition-opacity hover:opacity-80 disabled:cursor-wait disabled:opacity-60'
        aria-label={selected ? `Sub-status ${selected.label}` : 'Set sub-status'}
      >
        <SubStatusChip subStatus={selected} showChevron />
      </button>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', left: menuPos.left, top: menuPos.top, bottom: menuPos.bottom, width: MENU_WIDTH }}
            className='z-[1000] rounded-lg border border-[#D1D5D8] bg-white p-1 shadow-lg'
          >
            <div className='px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#6B7280]'>Set sub-status</div>
            {ORDERED_VALUES.map((val, idx) => {
              const opt = subStatusByValue(val)
              const isLast = ORDERED_VALUES[idx - 1] && subStatusByValue(ORDERED_VALUES[idx - 1])?.group !== opt.group
              return (
                <div key={val}>
                  {isLast && (
                    <div className='my-1 flex items-center gap-2 px-3 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]'>
                      <span className='flex-1 border-t border-[#EBEDF0]' />
                      Lost outcomes
                      <span className='flex-1 border-t border-[#EBEDF0]' />
                    </div>
                  )}
                  <button
                    type='button'
                    onClick={() => onPick(opt.value)}
                    className={`flex w-full items-center rounded px-2 py-1.5 text-left hover:bg-[#F8FAFC] ${
                      opt.value === value ? 'bg-[#F1F4F8]' : ''
                    }`}
                  >
                    <SubStatusChip subStatus={opt} />
                  </button>
                </div>
              )
            })}
          </div>,
          document.body
        )}
    </div>
  )
}

SubStatusPicker.propTypes = {
  quoteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentSubStatus: PropTypes.string,
  closedStatus: PropTypes.bool,
  onChange: PropTypes.func,
  setBy: PropTypes.string,
  setAt: PropTypes.string
}
